import React, {useCallback} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import EntryDAO from 'dao/EntryDAO';
import BaseCreateModal from 'folder-items/BaseCreateModal';
import {FolderDefaultsKey} from 'FolderDefaults';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import TemplateDAO from 'notebook/entry-templates/TemplateDAO';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import APIUtil from 'util/APIUtil';

export interface SaveAsDocumentResult {
  targetType: string;
  targetId: string;
  url: string;
}

export interface SaveAsDocumentModalProps {
  documentId: string;
  documentName: string;
  targetType: string;
  onRequestSubmit?: (result: SaveAsDocumentResult) => void | Promise<void>;
  onRequestClose: () => void;
  sourceDocumentId?: string;
}

export function SaveAsDocumentModal({
  documentId,
  documentName,
  targetType,
  onRequestSubmit,
  onRequestClose,
  sourceDocumentId,
}: SaveAsDocumentModalProps): JSX.Element {
  const handleSaveAs = useCallback(
    (
      name: string,
      parentApiId: string,
      tagSchemaId?: string,
      schemaTags?: TagUpdateObject
    ): JQueryPromise<string> => {
      const apiCall =
        targetType === ModelTypeConstants.ENTRY
          ? EntryDAO.createFromDocument({
              documentId,
              name: name.trim(),
              folderApiId: parentApiId,
              tagSchemaId,
              schemaTags,
              sourceTemplateId:
                sourceDocumentId && APIUtil.isEntryTemplate(sourceDocumentId) ? sourceDocumentId : undefined,
            })
          : TemplateDAO.createFromDocument({
              documentId,
              name: name.trim(),
              templateCollectionApiId: parentApiId,
              tagSchemaId,
              schemaTags,
            });

      return apiCall.then((result) => {
        if (!result) {
          throw new Error('Failed to save document');
        }
        SidepanelActions.close();
        onRequestSubmit?.({
          targetType,
          targetId: result.id,
          url: result.url,
        });
        return result.id;
      });
    },
    [documentId, targetType, onRequestSubmit, sourceDocumentId]
  );

  const modalTitle = targetType === ModelTypeConstants.ENTRY ? 'Save As Entry' : 'Save As Template';

  return (
    <BaseCreateModal
      create={handleSaveAs}
      title={modalTitle}
      initialName={documentName}
      onRequestClose={onRequestClose}
      showInventory={false}
      showNotebook={targetType === ModelTypeConstants.ENTRY}
      modelType={targetType}
      folderDefaultsKey={FolderDefaultsKey.NOTEBOOK}
      entryProvenance="entry"
      hideStudiesInput={true}
    />
  );
}
